import styled, { css } from 'styled-components'
import ChevronUpIcon from '../../../icons/IconLib/ChevronUpIcon'
import { GridColumn, SortState } from '../../types/grid.types'
import { CellAlign, resolveAlign } from '../../utils/align'

export interface HeadCellProps<T> {
  column: GridColumn<T>
  sortState: SortState<T>
  onSort: (key: keyof T) => void
}

export const HeadCell = <T,>({ column, sortState, onSort }: HeadCellProps<T>) => {
  const align = resolveAlign(column)
  const sortable = !!column.sortable
  const sorted = sortState?.key === column.key
  const desc = sorted && sortState?.dir === 'desc'

  return (
    <Th
      $align={align}
      $sortable={sortable}
      $sorted={sorted}
      $width={column.width}
      onClick={sortable ? () => onSort(column.key) : undefined}
      aria-sort={sorted ? (desc ? 'descending' : 'ascending') : undefined}
    >
      <Inner $align={align}>
        {column.title}
        {sortable && (
          <SortIco $sorted={sorted} $desc={desc}>
            <ChevronUpIcon size={13} />
          </SortIco>
        )}
      </Inner>
    </Th>
  )
}

export default HeadCell

const alignToText: Record<CellAlign, string> = {
  start: 'start',
  end: 'end',
  center: 'center',
}

const Th = styled.th<{
  $align: CellAlign
  $sortable: boolean
  $sorted: boolean
  $width?: string
}>`
  position: sticky;
  /* Pushed below a collapsible summary when GridTable sets it; 0 otherwise. */
  top: var(--grid-head-top, 0px);
  z-index: 2;
  background: ${({ theme }) => theme.colors.bgLayout};
  text-align: ${({ $align }) => alignToText[$align]};
  padding: 11px 16px;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme, $sorted }) => ($sorted ? theme.colors.ink : theme.colors.ink3)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  white-space: nowrap;
  user-select: none;
  ${({ $width }) => $width && css`width: ${$width};`}
  ${({ $sortable, theme }) =>
    $sortable &&
    css`
      cursor: pointer;
      transition: color ${theme.motion.fast} ease;
      &:hover {
        color: ${theme.colors.ink};
      }
    `}
`

const Inner = styled.span<{ $align: CellAlign }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  ${({ $align }) => $align === 'end' && css`flex-direction: row-reverse;`}
`

const SortIco = styled.span<{ $sorted: boolean; $desc: boolean }>`
  display: inline-flex;
  color: ${({ theme, $sorted }) => ($sorted ? theme.colors.ink : theme.colors.ink4)};
  opacity: ${({ $sorted }) => ($sorted ? 1 : 0)};
  transition:
    opacity ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease;
  ${({ $desc }) => $desc && css`transform: rotate(180deg);`}

  th:hover & {
    opacity: ${({ $sorted }) => ($sorted ? 1 : 0.6)};
  }
`
